import { SupplierTypeGroup } from "../../types/BuyerBriefTypes";

export interface TopicOption {
    id: string;
    label: string;
    description: string;
}

export const UNIVERSAL_TOPICS: TopicOption[] = [
    {
        id: "company_profile",
        label: "Company Profile",
        description: "History, size, leadership, and corporate structure",
    },
    {
        id: "financial_health",
        label: "Financial Health",
        description: "Revenue, profitability, debt levels, and financial stability",
    },
    {
        id: "reputation",
        label: "Reputation",
        description: "Market standing, reviews, and customer sentiment",
    },
    {
        id: "legal_compliance",
        label: "Legal & Compliance",
        description: "Regulatory status, litigation history, and compliance posture",
    },
    {
        id: "industry_landscape",
        label: "Industry Landscape",
        description: "Market dynamics, trends, and competitive positioning",
    },
    {
        id: "pricing",
        label: "Pricing",
        description: "Pricing models, competitiveness, and total cost of ownership",
    },
    {
        id: "relationship_support",
        label: "Relationship & Support",
        description: "Customer support quality, account management, and responsiveness",
    },
];

export const CONDITIONAL_TOPICS: Record<SupplierTypeGroup, TopicOption[]> = {
    [SupplierTypeGroup.SOFTWARE_SAAS]: [
        {
            id: "product_capabilities",
            label: "Product Capabilities",
            description: "Feature set, integrations, and API ecosystem",
        },
        {
            id: "security_compliance",
            label: "Security & Compliance",
            description: "SOC 2, ISO 27001, GDPR, and data handling",
        },
        {
            id: "pricing_model",
            label: "Pricing Model",
            description: "Per-seat, usage-based, and enterprise pricing analysis",
        },
        {
            id: "uptime_reliability",
            label: "Uptime & Reliability",
            description: "SLA history and infrastructure reliability",
        },
        {
            id: "data_portability",
            label: "Data Portability",
            description: "Lock-in factors, export capabilities, and migration support",
        },
    ],
    [SupplierTypeGroup.CLOUD_INFRASTRUCTURE]: [
        {
            id: "service_portfolio",
            label: "Service Portfolio",
            description: "Compute, storage, networking, and managed services breadth",
        },
        {
            id: "global_footprint",
            label: "Global Footprint",
            description: "Data center locations and edge network coverage",
        },
        {
            id: "security_certifications",
            label: "Security Certifications",
            description: "FedRAMP, HIPAA, PCI DSS, and compliance certifications",
        },
        {
            id: "pricing_reserved",
            label: "Pricing & Reserved Capacity",
            description: "On-demand vs. reserved pricing and discount tiers",
        },
        {
            id: "migration_tooling",
            label: "Migration Tooling",
            description: "Migration support, partner ecosystem, and multi-cloud capabilities",
        },
    ],
    [SupplierTypeGroup.PROFESSIONAL_SERVICES]: [
        {
            id: "domain_expertise",
            label: "Domain Expertise",
            description: "Practice areas and sector specialization",
        },
        {
            id: "key_personnel",
            label: "Key Personnel",
            description: "Partner qualifications and thought leadership",
        },
        {
            id: "billing_rates",
            label: "Billing & Rates",
            description: "Rate structures and fee competitiveness",
        },
        {
            id: "conflict_check",
            label: "Conflict of Interest",
            description: "Competitive engagement analysis",
        },
    ],
    [SupplierTypeGroup.HR_STAFFING]: [
        {
            id: "talent_pool",
            label: "Talent Pool",
            description: "Pool size, specializations, and geographic reach",
        },
        {
            id: "fill_metrics",
            label: "Fill Metrics",
            description: "Fill rate, time-to-fill, and quality metrics",
        },
        {
            id: "markup_rates",
            label: "Markup Rates",
            description: "Bill rate competitiveness and benchmarks",
        },
        {
            id: "compliance_labor",
            label: "Compliance",
            description: "Worker classification and labor law compliance",
        },
    ],
    [SupplierTypeGroup.MANUFACTURING_INDUSTRIAL]: [
        {
            id: "production_capacity",
            label: "Production Capacity",
            description: "Capacity, utilization, and lead times",
        },
        {
            id: "quality_certifications",
            label: "Quality Certifications",
            description: "ISO 9001, AS9100, and quality standards",
        },
        {
            id: "supply_chain",
            label: "Supply Chain",
            description: "Raw material sourcing and supply chain resilience",
        },
        {
            id: "defect_rates",
            label: "Defect Rates",
            description: "Quality control processes and defect metrics",
        },
    ],
    [SupplierTypeGroup.LOGISTICS_SUPPLY_CHAIN]: [
        {
            id: "network_coverage",
            label: "Network Coverage",
            description: "Geographic reach and route coverage",
        },
        {
            id: "delivery_performance",
            label: "Delivery Performance",
            description: "On-time rates and transit benchmarks",
        },
        {
            id: "technology_tracking",
            label: "Technology & Tracking",
            description: "Visibility tools and analytics platform",
        },
        {
            id: "warehousing",
            label: "Warehousing",
            description: "Storage capacity and fulfillment capabilities",
        },
    ],
    [SupplierTypeGroup.FACILITIES_REAL_ESTATE]: [
        {
            id: "service_scope",
            label: "Service Scope",
            description: "Coverage area and service breadth",
        },
        {
            id: "safety_record",
            label: "Safety Record",
            description: "Incident rates and safety performance",
        },
        {
            id: "sustainability_programs",
            label: "Sustainability",
            description: "Energy efficiency and green programs",
        },
        {
            id: "response_time",
            label: "Response Time",
            description: "SLA compliance and response metrics",
        },
    ],
    [SupplierTypeGroup.MARKETING_CREATIVE]: [
        {
            id: "creative_portfolio",
            label: "Creative Portfolio",
            description: "Campaign quality and creative capabilities",
        },
        {
            id: "digital_analytics",
            label: "Digital & Analytics",
            description: "MarTech stack and analytical depth",
        },
        {
            id: "category_experience",
            label: "Category Experience",
            description: "Industry-specific client work",
        },
        {
            id: "campaign_roi",
            label: "Campaign ROI",
            description: "Performance metrics and measurable outcomes",
        },
    ],
    [SupplierTypeGroup.FINANCIAL_SERVICES]: [
        {
            id: "regulatory_standing",
            label: "Regulatory Standing",
            description: "Compliance history and regulatory status",
        },
        {
            id: "product_breadth",
            label: "Product Breadth",
            description: "Service range and customization capability",
        },
        {
            id: "fee_structure",
            label: "Fee Structure",
            description: "Total cost comparison and fee transparency",
        },
        {
            id: "fiduciary_duties",
            label: "Fiduciary Duties",
            description: "Obligations and conflict management",
        },
    ],
    [SupplierTypeGroup.TELECOM_CONNECTIVITY]: [
        {
            id: "network_reliability",
            label: "Network Reliability",
            description: "Uptime data and coverage quality",
        },
        {
            id: "pricing_competitiveness",
            label: "Pricing",
            description: "Rate plans and competitive benchmarking",
        },
        {
            id: "technology_roadmap",
            label: "Technology Roadmap",
            description: "5G, SD-WAN, and UCaaS evolution",
        },
        {
            id: "contract_terms",
            label: "Contract Terms",
            description: "Lock-in periods and termination policies",
        },
    ],
    [SupplierTypeGroup.OTHER_CUSTOM]: [],
};
